// Waivers and free agents on a simulated league (WAIVERS.md): the pure
// engine from @fsp/waivers hosted by the sim. Claims, adds, drops, and the
// undo window are the same mutations the API will expose; processing runs
// at the settings' time as the clock passes it; other managers make a few
// deterministic moves each week so the log and the results have something
// to explain.

import {
  ON_WAIVERS_LIMIT,
  WAIVERS_LOG_LIMIT,
  arrangeRoster,
  lineupCells,
  placePlayer,
  type ClaimRequest,
  type ClaimResultView,
  type ClaimView,
  type DropDestination,
  type DropOption,
  type GameStatus,
  type LogEntry,
  type LogPage,
  type RosterMoveView,
  type TransactionView,
  type WaiverPlayerRow,
  type WaiversView,
} from '@fsp/domain';
import {
  addProblem,
  canMakeRoom,
  claimProblem,
  clearsAtAfterDraft,
  dropDestination,
  dropProblem,
  dropLockHolds,
  emptyCount,
  kickoffWaiverClearsAt,
  lastProcessingAt,
  nextProcessingAt,
  orderRolls,
  prioritySource,
  processingHeadline,
  resolveWaivers,
  reverseStandingsOrder,
  rosterProblem,
  rosterCapacity,
  type AcquisitionCount,
  type Claim,
  type ClaimResult,
  type DropTo,
  type StandingRow,
  type Transaction,
  type WaiverContext,
  claimStanding,
  dropConsequence as consequenceOf,
  dropSentence,
  noLineupSpotSentence,
  onWaiversSentence,
  priorityWhy,
  runChatLine,
  runSummary,
  transactionSummary as summarizeMove,
  undoneSentence,
} from '@fsp/waivers';
import { football } from '@fsp/sports-football';
import { lockFromKickoff } from '@fsp/clock';
import { systemMessage } from './chat';
import { gameFor, type SimPlayer, type SimState, type SimTeam } from './league';
import { hashSeed, rng } from './rng';
import { keepWeek } from './past-weeks';
import {
  basisFor,
  gameStatus,
  leagueRef,
  playerSummary,
  projection,
  standingsView,
  teamRef,
} from './views';

const H = 3600 * 1000;
const UNDO_MS = 5 * 60 * 1000;
/** Cleared players stay in the On waivers list this long, marked "free agent since". */
const CLEARED_SHOWN_MS = 3 * 24 * H;

export interface WaiverEntry {
  clearsAt: number;
  droppedBy: string | null;
  droppedAt: number;
}

export interface WaiverRun {
  id: string;
  at: number;
  results: ClaimResult[];
}

export interface Acquisition {
  teamId: string;
  playerId: string;
  position: string;
  at: number;
  period: number;
  transactionId: string;
}

/** A move another manager will make when the clock gets there. */
export type PlannedMove =
  | { at: number; kind: 'drop'; teamId: string; playerId: string }
  | { at: number; kind: 'add'; teamId: string; playerId: string; dropPlayerId: string }
  | {
      at: number;
      kind: 'claim';
      teamId: string;
      playerId: string;
      bid: number | null;
      dropPlayerId: string;
    };

export interface WaiverState {
  onWaivers: Map<string, WaiverEntry>;
  claims: Claim[];
  transactions: Transaction[];
  runs: WaiverRun[];
  /** Rolling priority, best first. Reverse-standings recomputes from standings. */
  priority: string[];
  acquisitions: Acquisition[];
  planned: PlannedMove[];
  /** The last processing instant handled. */
  lastRunAt: number;
}

function iso(ms: number): string {
  return new Date(ms).toISOString();
}

function zone(state: SimState): string {
  return state.settings.identity.timeZone;
}

/** The zone a time is WRITTEN in for this state's reader (B-181); `zone` still decides every rule. */
function labelZone(state: SimState): string {
  return state.readerZone ?? zone(state);
}

// ── Context ───────────────────────────────────────────────────────────────

const slotDefs = new Map(football.slots.map((s) => [s.id, s]));

/**
 * Names, positions and the roster shape for the engine and the validators, and
 * the lineup's `fits`: whether a roster that counts right also lays out in the
 * team's lineup (`arrangeRoster`), the check a real league makes over its
 * rows. `now`, when given, keeps a started bench player from being moved up.
 */
export function waiverContext(state: SimState, now?: number): WaiverContext {
  const positionOf = (id: string) => state.players.get(id)?.positions[0] ?? '';
  const roster = {
    slots: state.settings.roster.slots.map(({ slot, count }) => {
      const def = slotDefs.get(slot);
      return { id: slot, count, kind: def?.kind ?? 'bench', eligible: def?.eligible ?? [] };
    }),
    maxPerPosition: state.settings.roster.maxPerPosition,
    allowEmptySlots: state.settings.lineup.allowEmptySlots,
  };
  return {
    positionOf,
    playerName: (id) => state.players.get(id)?.name ?? 'somebody',
    teamName: (id) => state.teams.find((t) => t.id === id)?.name ?? 'a team',
    roster,
    fits: (teamId, after) => {
      const team = state.teams.find((t) => t.id === teamId);
      if (!team) return null;
      const locked = now === undefined ? new Set<string>() : lockedPlayers(state, now);
      const cells = lineupCells(
        roster,
        team.lineup.map((r) => ({ ...r, stamped: false })),
      );
      const { unplaced } = arrangeRoster(cells, roster, positionOf, (id) => locked.has(id), after);
      return unplaced === null
        ? null
        : noLineupSpotSentence(state.players.get(unplaced)?.name ?? 'that player');
    },
  };
}

/**
 * When this period's lineup locks whole, under a league whose lineups lock at
 * the week's first game or at a fixed time (`lineup.lockRule`), by the clock's
 * own `lockFromKickoff`; null when each player locks at his own game.
 */
export function lineupLockAt(state: SimState): number | null {
  if (state.proGames.length === 0) return null;
  const first = Math.min(...state.proGames.map((g) => g.startAt));
  const at = lockFromKickoff(state.settings, new Date(first).toISOString());
  return at === null ? null : Date.parse(at);
}

/**
 * Whether the game-time lock holds this player, until the period ends: his
 * game has kicked off, or he is on a roster whose lineup has locked whole —
 * the league store's `isLocked`, rule for rule (B-148). A free agent is
 * locked by his own game only.
 */
function lockedNow(
  state: SimState,
  player: SimPlayer,
  now: number,
  lineupLock: number | null,
): boolean {
  if (!state.settings.waivers.gameTimeLock || now >= state.periodEnd) return false;
  const g = gameFor(state, player);
  if (g && now >= g.startAt) return true;
  if (lineupLock === null || now < lineupLock) return false;
  return ownerOf(state, player.id) !== null;
}

/** Players frozen by the game-time lock: kicked off (or locked in a whole lineup), period not over. */
export function lockedPlayers(state: SimState, now: number): Set<string> {
  const out = new Set<string>();
  if (!state.settings.waivers.gameTimeLock || now >= state.periodEnd) return out;
  const lineupLock = lineupLockAt(state);
  for (const p of state.players.values()) if (lockedNow(state, p, now, lineupLock)) out.add(p.id);
  return out;
}

/**
 * When this player comes off waivers, or null when the player is not on them
 * at `now`. Two things put a player there: a drop or the draft (the
 * `onWaivers` entry, until it clears), and, for a free agent, a game that has
 * started, from the kickoff to the first run after the period
 * (`kickoffWaiverClearsAt`, PLAN-2026-09-24 §2 rule 6). Where both hold, the
 * later clear time. A player on a roster is never on waivers; the lock keeps a
 * started player of mine from being dropped, which is `dropProblem`'s
 * business, not this.
 *
 * The kickoff's waivers are read off the game and the period, not off the
 * lock: the lock lifts at the period's end, an hour before the run, and this
 * read the lock until the W3 review, so for that hour a started free agent was
 * Add again and every claim on him was decided `gone`.
 *
 * The one answer for the pool, the Waivers list, the sheets, the add's
 * refusal, a claim's placement and which claims a run decides.
 */
export function waiverClearsAt(state: SimState, playerId: string, now: number): number | null {
  const e = state.waiverState.onWaivers.get(playerId);
  const dropped = e && e.clearsAt > now ? e.clearsAt : null;
  const player = state.players.get(playerId);
  const game = player ? gameFor(state, player) : null;
  const kickoff =
    game && !ownerOf(state, playerId)
      ? kickoffWaiverClearsAt(
          state.settings.waivers,
          zone(state),
          game.startAt,
          state.periodEnd,
          now,
        )
      : null;
  if (dropped === null) return kickoff;
  return kickoff === null ? dropped : Math.max(dropped, kickoff);
}

function used(state: SimState, teamId: string): AcquisitionCount {
  const out = emptyCount();
  for (const a of state.waiverState.acquisitions) {
    if (a.teamId !== teamId) continue;
    out.season++;
    if (a.period === state.currentPeriod) out.period++;
    out.byPosition[a.position] = (out.byPosition[a.position] ?? 0) + 1;
  }
  return out;
}

/**
 * The draft's order, first pick first: the room's, which every league in the
 * simulator has once it has drafted (a seeded league's is its seats' snake).
 * The seats' order stands in for a league that has not drafted.
 */
function draftOrder(state: SimState): string[] {
  return state.draft?.state.order ?? state.teams.map((t) => t.id);
}

/** The reverse of the draft order: the team that picked last first (PLAN-2026-09-24 §2 rule 2). */
function reverseDraftOrder(state: SimState): string[] {
  return [...draftOrder(state)].reverse();
}

/**
 * The standings the waiver engine reads, IN REVERSE DRAFT ORDER. The engine
 * breaks a level record and level points by the order the rows came in, so
 * before any game is final (every team 0–0) reverse standings is the reverse
 * of the draft, and so is any later dead heat (rule 2). They came in the
 * table's own order until W3, which at 0–0 put the first pick first.
 *
 * The regular season's table only: `standingsView` counts no playoff game, so
 * from the first playoff week this is the final regular-season table and the
 * order holds still through the bracket.
 */
function standingRows(state: SimState, now: number): StandingRow[] {
  const seat = new Map(reverseDraftOrder(state).map((id, i) => [id, i]));
  const at = (id: string) => seat.get(id) ?? Number.MAX_SAFE_INTEGER;
  return standingsView(state, now)
    .rows.map((r) => ({
      teamId: r.team.id,
      wins: r.wins,
      losses: r.losses,
      ties: r.ties,
      pointsFor: r.pointsFor,
    }))
    .sort((a, b) => at(a.teamId) - at(b.teamId));
}

/**
 * The order the next run would read, best first: the rolling order when a win
 * moves a team back (`orderRolls`), or else reverse standings from the
 * regular season's table, fed in reverse draft order (`standingRows`). What
 * the Waivers header's rank is read from.
 */
export function waiverOrder(state: SimState, now: number): string[] {
  if (orderRolls(state.settings.waivers)) return [...state.waiverState.priority];
  return reverseStandingsOrder(standingRows(state, now));
}

/**
 * NO CALENDAR, so no run of the simulator's moves (B-507): its week ends at
 * 2:00 AM Wednesday every week, before the run, so none of its weeks runs late.
 */
export function processesAt(state: SimState, from: number): number | null {
  if (state.settings.waivers.system === 'none') return null;
  return nextProcessingAt(state.settings.waivers.processing, zone(state), from);
}

/**
 * When a claim on this player is decided: the run at or after he clears, or on
 * a continuous schedule the moment he clears. A claim's drop happens then, so
 * that is when the 24-hour rule and the game-time lock judge it — the sheet's
 * drop options, a claim's placement and its standing all read this.
 */
function claimDecidedAt(state: SimState, playerId: string, now: number): number {
  const clears =
    waiverClearsAt(state, playerId, now) ??
    state.waiverState.onWaivers.get(playerId)?.clearsAt ??
    now;
  return processesAt(state, Math.max(now, clears - 1)) ?? Math.max(now, clears);
}

function myTeam(state: SimState): SimTeam {
  return state.teams.find((t) => t.isMine)!;
}

function pendingOf(state: SimState, teamId: string): Claim[] {
  return state.waiverState.claims
    .filter((c) => c.teamId === teamId && c.status === 'pending')
    .sort((a, b) => a.order - b.order);
}

function onWaiversNow(state: SimState, playerId: string, now: number): boolean {
  return waiverClearsAt(state, playerId, now) !== null;
}

function ownerOf(state: SimState, playerId: string): SimTeam | null {
  return state.teams.find((t) => t.roster.includes(playerId)) ?? null;
}

// ── Roster changes ────────────────────────────────────────────────────────

/**
 * When this team added the player from free agency, if that is how the player
 * came: the newest move of the team's that brought the player in and was not
 * undone. Null for a player who came by the draft, a claim or a trade.
 */
function addedFromFreeAgencyAt(state: SimState, teamId: string, playerId: string): number | null {
  let newest: Transaction | null = null;
  for (const tx of state.waiverState.transactions) {
    if (tx.teamId !== teamId || tx.reversedBy || !tx.added.includes(playerId)) continue;
    if (!newest || tx.at >= newest.at) newest = tx;
  }
  return newest?.kind === 'add' ? newest.at : null;
}

/**
 * Where a player this team drops at `at` goes: `dropDestination` from the
 * engine package, fed this team's add of the player. The move itself and every
 * sheet that says where the player will go read it, so they cannot disagree.
 */
function dropTo(state: SimState, teamId: string, playerId: string, at: number): DropTo {
  return dropDestination(
    state.settings.waivers,
    zone(state),
    at,
    addedFromFreeAgencyAt(state, teamId, playerId),
  );
}

function dropToView(to: DropTo): DropDestination {
  return to.kind === 'waivers' ? { kind: 'waivers', clearsAt: iso(to.clearsAt) } : to;
}

/**
 * Apply one move to a team: dropped players leave the roster and lineup and
 * go on waivers (or straight to free agency, `dropTo`); added players join the
 * roster and take a bench row, or an eligible empty starter row, without ever
 * leaving a starter row holding an ineligible player.
 */
export function applyMove(
  state: SimState,
  team: SimTeam,
  added: readonly string[],
  dropped: readonly string[],
  at: number,
): void {
  for (const id of dropped) {
    const to = dropTo(state, team.id, id, at);
    team.roster = team.roster.filter((x) => x !== id);
    for (const row of team.lineup) if (row.playerId === id) row.playerId = null;
    if (to.kind === 'waivers')
      state.waiverState.onWaivers.set(id, {
        clearsAt: to.clearsAt,
        droppedBy: team.ownerName,
        droppedAt: at,
      });
    else state.waiverState.onWaivers.delete(id);
  }
  // THE ONE LAYOUT (B-30): the domain's `placePlayer`, which `fits` above
  // asks through `arrangeRoster`, so a move the check lets through is seated
  // as the check saw it. Its own copy of the rule here missed the rule's
  // fourth step, a starter moving over from the flex, and seated a receiver
  // in the tight end's row (the review of the trade review's chunk 4).
  const w = waiverContext(state);
  const locked = lockedPlayers(state, at);
  for (const id of added) {
    state.waiverState.onWaivers.delete(id);
    if (!team.roster.includes(id)) team.roster.push(id);
    const cells = team.lineup.map((r) => ({ ...r, stamped: false }));
    if (placePlayer(cells, w.roster, w.positionOf, (pid) => locked.has(pid), id)) {
      cells.forEach((c, i) => (team.lineup[i]!.playerId = c.playerId));
      continue;
    }
    // NO ROW HE CAN PLAY, which a pickup (`fits`) and a trade (`unseatable`,
    // B-480) both refuse before they get here; what is left is a layout the
    // four steps cannot reach in one move, and he takes the first open row,
    // as he always has.
    const empty = team.lineup.find((r) => r.playerId === null);
    if (empty) empty.playerId = id;
  }
}

// ── Mutations ─────────────────────────────────────────────────────────────

/** Ids are deterministic per league so a replayed journal and a twin world agree. */
function nextId(state: SimState, kind: 'claim' | 'tx' | 'undo', at: number): string {
  const ws = state.waiverState;
  const n =
    kind === 'claim'
      ? ws.claims.length
      : kind === 'tx'
        ? ws.transactions.length
        : ws.transactions.filter((t) => t.reversedBy).length;
  return `${state.leagueId}-${kind}-${at}-${n}`;
}

/**
 * A claim as the simulator takes it: the API's `ClaimRequest` body, plus the
 * team the API reads from the session and the claim an edit's path names. One
 * shape, so the screens hand the practice league what they will hand the site.
 */
export type ClaimInput = ClaimRequest & {
  teamId: string;
  /** Set to edit an existing pending claim in place. */
  claimId?: string;
};

export type ClaimResultOk = { ok: true; claim: Claim };
export type MoveRefused = { ok: false; reason: string };

/** Place or edit a claim. Validated now; validated again at processing. */
export function placeClaim(
  state: SimState,
  input: ClaimInput,
  now: number,
): ClaimResultOk | MoveRefused {
  const team = state.teams.find((t) => t.id === input.teamId);
  if (!team) return { ok: false, reason: 'Unknown team.' };
  if (!state.players.has(input.playerId)) return { ok: false, reason: 'Unknown player.' };
  const ctx = waiverContext(state, now);
  const mine = pendingOf(state, team.id);
  const editing = input.claimId ? (mine.find((c) => c.id === input.claimId) ?? null) : null;
  if (input.claimId && !editing) return { ok: false, reason: 'That claim is no longer pending.' };
  const bid = state.settings.waivers.system === 'faab' ? (input.bid ?? 0) : null;
  const problem = claimProblem(
    {
      rules: state.settings.waivers,
      limits: state.settings.acquisitions,
      teamId: team.id,
      roster: team.roster,
      locked: lockedPlayers(state, now),
      acquisitionsUsed: used(state, team.id),
      playerId: input.playerId,
      bid,
      dropPlayerIds: input.dropPlayerIds,
      conditionalOnClaimId: input.conditionalOnClaimId,
      budget: team.faabRemaining,
      myClaims: mine,
      editingClaimId: editing?.id ?? null,
      onWaivers: onWaiversNow(state, input.playerId, now),
      decidedAt: claimDecidedAt(state, input.playerId, now),
      lockEndsAt: state.periodEnd,
    },
    ctx,
  );
  if (problem) return { ok: false, reason: problem };
  const others = mine.filter((c) => c.id !== editing?.id);
  const order = Math.max(
    1,
    Math.min(others.length + 1, input.order ?? editing?.order ?? others.length + 1),
  );
  const claim: Claim = editing ?? {
    id: nextId(state, 'claim', now),
    teamId: team.id,
    playerId: input.playerId,
    bid,
    dropPlayerIds: [],
    order,
    conditionalOnClaimId: null,
    placedAt: now,
    status: 'pending',
  };
  claim.bid = bid;
  claim.dropPlayerIds = [...input.dropPlayerIds];
  claim.conditionalOnClaimId = input.conditionalOnClaimId;
  if (!editing) state.waiverState.claims.push(claim);
  // Renumber: the claim takes `order`, the rest keep their relative order.
  const ordered = others.slice();
  ordered.splice(order - 1, 0, claim);
  ordered.forEach((c, i) => (c.order = i + 1));
  return { ok: true, claim };
}

export function withdrawClaim(state: SimState, claimId: string): string | null {
  const claim = state.waiverState.claims.find((c) => c.id === claimId);
  if (!claim || claim.status !== 'pending') return 'That claim is no longer pending.';
  claim.status = 'withdrawn';
  for (const c of pendingOf(state, claim.teamId))
    if (c.conditionalOnClaimId === claim.id) c.conditionalOnClaimId = null;
  pendingOf(state, claim.teamId).forEach((c, i) => (c.order = i + 1));
  return null;
}

/** Set the order of a team's pending claims; ids missing from the list keep their relative place at the end. */
export function reorderClaims(state: SimState, teamId: string, claimIds: string[]): string | null {
  const mine = pendingOf(state, teamId);
  const known = new Set(mine.map((c) => c.id));
  if (claimIds.some((id) => !known.has(id))) return 'That claim is no longer pending.';
  const ordered = [
    ...claimIds.map((id) => mine.find((c) => c.id === id)!),
    ...mine.filter((c) => !claimIds.includes(c.id)),
  ];
  ordered.forEach((c, i) => (c.order = i + 1));
  return null;
}

/** Add a free agent, dropping as needed. One transaction, one chat line, undo for five minutes. */
export function addPlayer(
  state: SimState,
  input: { teamId: string; playerId: string; dropPlayerIds: string[] },
  now: number,
): { ok: true; transaction: Transaction } | MoveRefused {
  const team = state.teams.find((t) => t.id === input.teamId);
  if (!team) return { ok: false, reason: 'Unknown team.' };
  const player = state.players.get(input.playerId);
  if (!player) return { ok: false, reason: 'Unknown player.' };
  const owner = ownerOf(state, input.playerId);
  if (owner) return { ok: false, reason: `${player.name} is on ${owner.name}.` };
  if (onWaiversNow(state, input.playerId, now))
    return { ok: false, reason: `${player.name} is on waivers. Put in a claim instead.` };
  const problem = addProblem(
    {
      rules: state.settings.waivers,
      limits: state.settings.acquisitions,
      teamId: team.id,
      roster: team.roster,
      locked: lockedPlayers(state, now),
      acquisitionsUsed: used(state, team.id),
      playerId: input.playerId,
      dropPlayerIds: input.dropPlayerIds,
    },
    waiverContext(state, now),
  );
  if (problem) return { ok: false, reason: problem };
  const tx: Transaction = {
    id: nextId(state, 'tx', now),
    at: now,
    kind: 'add',
    teamId: team.id,
    added: [input.playerId],
    dropped: [...input.dropPlayerIds],
    faab: null,
    reason: null,
    reversedBy: null,
    claimId: null,
    runId: null,
  };
  applyMove(state, team, tx.added, tx.dropped, now);
  state.waiverState.transactions.push(tx);
  state.waiverState.acquisitions.push({
    teamId: team.id,
    playerId: input.playerId,
    position: player.positions[0],
    at: now,
    period: state.currentPeriod,
    transactionId: tx.id,
  });
  // THE ADD'S DROPS ARE DROPS. A pending claim that meant to drop one of them
  // can no longer, exactly as after a plain drop; until W5 only `dropPlayer`
  // cleared them, so a claim here kept a drop that had already left the roster
  // and lost at the run as `roster-full` for a reason the sheet never showed.
  for (const c of pendingOf(state, team.id))
    c.dropPlayerIds = c.dropPlayerIds.filter((id) => !tx.dropped.includes(id));
  state.chat.push(systemMessage(state, now, transactionSummary(state, tx)));
  return { ok: true, transaction: tx };
}

export function dropPlayer(
  state: SimState,
  input: { teamId: string; playerId: string },
  now: number,
): { ok: true; transaction: Transaction } | MoveRefused {
  const team = state.teams.find((t) => t.id === input.teamId);
  if (!team) return { ok: false, reason: 'Unknown team.' };
  const problem = dropProblem(
    {
      rules: state.settings.waivers,
      limits: state.settings.acquisitions,
      teamId: team.id,
      roster: team.roster,
      locked: lockedPlayers(state, now),
      acquisitionsUsed: used(state, team.id),
      playerId: input.playerId,
    },
    waiverContext(state, now),
  );
  if (problem) return { ok: false, reason: problem };
  const tx: Transaction = {
    id: nextId(state, 'tx', now),
    at: now,
    kind: 'drop',
    teamId: team.id,
    added: [],
    dropped: [input.playerId],
    faab: null,
    reason: null,
    reversedBy: null,
    claimId: null,
    runId: null,
  };
  applyMove(state, team, [], tx.dropped, now);
  state.waiverState.transactions.push(tx);
  // A pending claim that meant to drop this player can no longer.
  for (const c of pendingOf(state, team.id))
    c.dropPlayerIds = c.dropPlayerIds.filter((id) => id !== input.playerId);
  state.chat.push(systemMessage(state, now, transactionSummary(state, tx)));
  return { ok: true, transaction: tx };
}

/** Why a transaction cannot be undone right now, or null when it can. */
export function undoProblem(state: SimState, tx: Transaction, now: number): string | null {
  if (tx.kind !== 'add' && tx.kind !== 'drop') return 'Only adds and drops can be undone.';
  if (tx.reversedBy) return 'Already undone.';
  if (now - tx.at > UNDO_MS) return 'The five-minute undo window has passed.';
  const team = state.teams.find((t) => t.id === tx.teamId)!;
  // THE ROSTER IT PUTS BACK HAS TO BE ONE: a drop undone after the spot it
  // freed was filled would be a sixteenth player on a roster of fifteen. Until
  // W5 nothing looked (the league store's undo checks the same).
  const ctx = waiverContext(state, now);
  const after = [...team.roster.filter((id) => !tx.added.includes(id)), ...tx.dropped];
  const room =
    rosterProblem(after, ctx.roster, ctx.positionOf) ?? ctx.fits?.(team.id, after) ?? null;
  if (room) return `Not with the roster as it is now. ${room}`;
  // THE LOCK HOLDS AN UNDO AS IT HOLDS A DROP OR AN ADD: an added player whose
  // game has started stays, and a dropped one whose game has started does not
  // come back into it, until the period ends — the league store's rule.
  const locked = lockedPlayers(state, now);
  for (const id of [...tx.added, ...tx.dropped])
    if (locked.has(id)) return `${state.players.get(id)?.name} is locked until the week ends.`;
  for (const id of tx.added)
    if (!team.roster.includes(id)) return `${state.players.get(id)?.name} has already moved on.`;
  for (const id of tx.dropped) {
    const owner = ownerOf(state, id);
    if (owner) return `${state.players.get(id)?.name} is already on ${owner.name}.`;
    const claimed = state.waiverState.claims.some(
      (c) => c.status === 'pending' && c.playerId === id && c.teamId !== tx.teamId,
    );
    if (claimed) return `Someone has already claimed ${state.players.get(id)?.name}.`;
  }
  return null;
}

export function undoTransaction(
  state: SimState,
  transactionId: string,
  now: number,
): string | null {
  const tx = state.waiverState.transactions.find((t) => t.id === transactionId);
  if (!tx) return 'Unknown transaction.';
  const problem = undoProblem(state, tx, now);
  if (problem) return problem;
  const team = state.teams.find((t) => t.id === tx.teamId)!;
  // Reverse: the added leave, the dropped come back off waivers.
  for (const id of tx.added) {
    team.roster = team.roster.filter((x) => x !== id);
    for (const row of team.lineup) if (row.playerId === id) row.playerId = null;
  }
  for (const id of tx.dropped) state.waiverState.onWaivers.delete(id);
  applyMove(state, team, tx.dropped, [], now);
  tx.reversedBy = nextId(state, 'undo', now);
  state.waiverState.acquisitions = state.waiverState.acquisitions.filter(
    (a) => a.transactionId !== tx.id,
  );
  const back = tx.dropped.map((id) => state.players.get(id)?.name ?? 'somebody');
  state.chat.push(systemMessage(state, now, undoneSentence(team.ownerName, back)));
  return null;
}

// ── Processing ────────────────────────────────────────────────────────────

/** Run the engine at `at` over every claim whose player has cleared, apply, log, narrate. */
function runWaivers(state: SimState, at: number): void {
  const ws = state.waiverState;
  const rules = state.settings.waivers;
  const ready = ws.claims.filter(
    (c) => c.status === 'pending' && waiverClearsAt(state, c.playerId, at) === null,
  );
  ws.lastRunAt = at;
  if (ready.length === 0) return;
  const runId = `${state.leagueId}-run-${at}`;
  const out = resolveWaivers(
    {
      rules,
      limits: state.settings.acquisitions,
      claims: ready,
      rosters: Object.fromEntries(state.teams.map((t) => [t.id, t.roster])),
      budgets: Object.fromEntries(state.teams.map((t) => [t.id, t.faabRemaining])),
      priority: ws.priority,
      standings: standingRows(state, at),
      locked: lockedPlayers(state, at),
      acquisitionsUsed: Object.fromEntries(state.teams.map((t) => [t.id, used(state, t.id)])),
      clearsAt: Object.fromEntries([...ws.onWaivers.entries()].map(([id, e]) => [id, e.clearsAt])),
      now: at,
      runId,
    },
    waiverContext(state, at),
  );
  for (const tx of out.transactions) {
    const team = state.teams.find((t) => t.id === tx.teamId)!;
    applyMove(state, team, tx.added, tx.dropped, at);
    ws.transactions.push(tx);
    for (const id of tx.added)
      ws.acquisitions.push({
        teamId: team.id,
        playerId: id,
        position: state.players.get(id)?.positions[0] ?? '',
        at,
        period: state.currentPeriod,
        transactionId: tx.id,
      });
  }
  for (const t of state.teams) t.faabRemaining = out.budgets[t.id] ?? t.faabRemaining;
  ws.priority = out.priority;
  for (const r of out.results) {
    const c = ws.claims.find((x) => x.id === r.claimId);
    if (c) c.status = r.outcome === 'won' ? 'won' : 'lost';
  }
  ws.runs.push({ id: runId, at, results: out.results });
  const won = out.results.filter((r) => r.outcome === 'won').length;
  state.chat.push(systemMessage(state, at, runChatLine(won, out.results.length)));
  for (const tx of out.transactions)
    state.chat.push(systemMessage(state, at + 1000, transactionSummary(state, tx)));
}

function executePlanned(state: SimState, move: PlannedMove): void {
  const team = state.teams.find((t) => t.id === move.teamId);
  if (!team) return;
  if (move.kind === 'drop')
    dropPlayer(state, { teamId: team.id, playerId: move.playerId }, move.at);
  else if (move.kind === 'add')
    addPlayer(
      state,
      { teamId: team.id, playerId: move.playerId, dropPlayerIds: [move.dropPlayerId] },
      move.at,
    );
  else {
    let playerId = move.playerId;
    if (!onWaiversNow(state, playerId, move.at)) {
      const owned = new Set(state.teams.flatMap((t) => t.roster));
      const claimed = new Set(pendingOf(state, team.id).map((c) => c.playerId));
      const best = [...state.waiverState.onWaivers.entries()]
        .filter(([id, e]) => e.clearsAt > move.at && !owned.has(id) && !claimed.has(id))
        .map(([id]) => state.players.get(id)!)
        .filter((p) => !p.injury)
        .sort((a, b) => b.ppg - a.ppg)[0];
      if (!best) return;
      playerId = best.id;
    }
    placeClaim(
      state,
      {
        teamId: team.id,
        playerId,
        bid: move.bid,
        dropPlayerIds: team.roster.includes(move.dropPlayerId) ? [move.dropPlayerId] : [],
        conditionalOnClaimId: null,
      },
      move.at,
    );
  }
}

/**
 * Move waivers forward to `now`: other managers' planned moves happen as
 * their times pass, processing runs at each scheduled instant, cleared
 * players age out of the list. Returns true when anything changed.
 */
export function advanceWaivers(state: SimState, now: number): boolean {
  if (state.status !== 'in-season') return false;
  const ws = state.waiverState;
  let changed = false;
  const step = (until: number) => {
    const due = ws.planned.filter((m) => m.at <= until).sort((a, b) => a.at - b.at);
    if (due.length === 0) return;
    ws.planned = ws.planned.filter((m) => m.at > until);
    for (const m of due) executePlanned(state, m);
    changed = true;
  };
  if (state.settings.waivers.system !== 'none') {
    if (state.settings.waivers.processing.schedule === 'continuous') {
      step(now);
      const due = [...ws.onWaivers.entries()].filter(
        ([, e]) => e.clearsAt <= now && e.clearsAt > ws.lastRunAt,
      );
      if (due.length) {
        runWaivers(state, now);
        planWeek(state, now);
        changed = true;
      }
    } else {
      for (let guard = 0; guard < 60; guard++) {
        const next = nextProcessingAt(state.settings.waivers.processing, zone(state), ws.lastRunAt);
        if (next === null || next > now) break;
        step(next);
        runWaivers(state, next);
        planWeek(state, next);
        changed = true;
      }
    }
  }
  step(now);
  for (const [id, e] of ws.onWaivers)
    if (e.clearsAt + CLEARED_SHOWN_MS <= now) {
      ws.onWaivers.delete(id);
      changed = true;
    }
  return changed;
}

// ── Other managers ────────────────────────────────────────────────────────

/**
 * A bench skill player a manager would cut to make room: one of the three
 * least valuable, so the wire gets names worth a bid, not only the dregs.
 */
function worstBench(
  state: SimState,
  team: SimTeam,
  exclude: ReadonlySet<string>,
  pick = 0,
): string | null {
  const justAdded = new Set(
    state.waiverState.acquisitions
      .filter((a) => a.teamId === team.id && a.period === state.currentPeriod)
      .map((a) => a.playerId),
  );
  const bench = team.lineup
    .filter((r) => r.playerId && slotDefs.get(r.slot)?.kind === 'bench')
    .map((r) => state.players.get(r.playerId!)!)
    .filter((p) => !exclude.has(p.id) && !justAdded.has(p.id))
    .filter((p) => p.positions[0] !== 'K' && p.positions[0] !== 'DST');
  bench.sort((a, b) => a.ppg - b.ppg);
  return (bench[Math.min(pick, bench.length - 1)] ?? bench[0])?.id ?? null;
}

function bestAvailable(
  state: SimState,
  exclude: ReadonlySet<string>,
  count: number,
  at: number,
): SimPlayer[] {
  const owned = new Set(state.teams.flatMap((t) => t.roster));
  return [...state.players.values()]
    .filter((p) => !owned.has(p.id) && !exclude.has(p.id) && !p.injury)
    .filter((p) => !onWaiversNow(state, p.id, at))
    .filter((p) => p.positions[0] !== 'K' && p.positions[0] !== 'DST')
    .sort((a, b) => b.ppg - a.ppg)
    .slice(0, count);
}

/**
 * Plan the week after `runAt`: a few drops and adds by other managers on
 * Thursday through Saturday, then claims on whoever is on waivers, placed
 * before the next processing. Deterministic by seed and week.
 */
function planWeek(state: SimState, runAt: number): void {
  const ws = state.waiverState;
  const next = processesAt(state, runAt);
  const r = rng(hashSeed(state.seed, 'waivers', runAt));
  const others = state.teams.filter((t) => !t.isMine && t.ownerName);
  if (others.length === 0) return;
  const touched = new Set<string>();
  const dropTimes = [runAt + 31 * H, runAt + 55 * H, runAt + 80 * H]; // Thu 10 AM, Fri 10 AM, Sat 11 AM
  // Three managers churn the bottom of their bench for a free agent. A
  // manager with an Out player on the bench cuts him loose outright.
  const droppers = r.shuffle(others).slice(0, 3);
  droppers.forEach((team, i) => {
    const at = dropTimes[i]!;
    const hurt = team.lineup
      .filter((row) => row.playerId && slotDefs.get(row.slot)?.kind === 'bench')
      .map((row) => state.players.get(row.playerId!)!)
      .find((p) => p.injury?.status === 'O' && !touched.has(p.id));
    if (hurt) {
      touched.add(hurt.id);
      ws.planned.push({ at, kind: 'drop', teamId: team.id, playerId: hurt.id });
      return;
    }
    const drop = worstBench(state, team, touched, r.int(0, 2));
    const pick = bestAvailable(state, touched, 6, at)[r.int(0, 5)];
    if (!drop || !pick) return;
    touched.add(drop);
    touched.add(pick.id);
    ws.planned.push({ at, kind: 'add', teamId: team.id, playerId: pick.id, dropPlayerId: drop });
  });
  // Claims: the best players on waivers now (and those about to be dropped) draw two or three bidders each.
  if (state.settings.waivers.system === 'none' || next === null) return;
  const waiting = [...ws.onWaivers.entries()]
    .filter(([, e]) => e.clearsAt > runAt)
    .map(([id]) => state.players.get(id)!)
    .sort((a, b) => b.ppg - a.ppg)
    .slice(0, 4);
  const dropAt = new Map<string, number>();
  for (const m of ws.planned) {
    if (m.at <= runAt) continue;
    const id = m.kind === 'drop' ? m.playerId : m.kind === 'add' ? m.dropPlayerId : null;
    if (id) dropAt.set(id, m.at);
  }
  const targets = [...waiting, ...[...dropAt.keys()].map((id) => state.players.get(id)!)]
    .filter((p, i, a) => a.indexOf(p) === i && !p.injury && !state.byes.includes(p.proTeam))
    .sort((a, b) => b.ppg - a.ppg)
    .slice(0, 5);
  targets.forEach((p, i) => {
    const bidders = r.shuffle(others).slice(0, i === 0 ? 3 : i === 1 ? 2 : 1);
    bidders.forEach((team, j) => {
      const drop = worstBench(state, team, touched, r.int(0, 1));
      if (!drop) return;
      touched.add(drop);
      const earliest = (dropAt.get(p.id) ?? runAt) + 2 * H;
      const bid =
        state.settings.waivers.system === 'faab'
          ? Math.max(
              state.settings.waivers.faab?.minBid ?? 0,
              Math.min(team.faabRemaining, Math.round((p.ppg - 4) * 3 + r.around(0, 4))),
            )
          : null;
      ws.planned.push({
        at: Math.min(
          next - 2 * H,
          Math.max(earliest + j * 3 * H, runAt + 40 * H + (i * 7 + j * 5) * H + r.int(0, 3) * H),
        ),
        kind: 'claim',
        teamId: team.id,
        playerId: p.id,
        bid: bid === null ? null : Math.max(0, bid),
        dropPlayerId: drop,
      });
    });
  });
}

// ── Setup ─────────────────────────────────────────────────────────────────

export function emptyWaiverState(): WaiverState {
  return {
    onWaivers: new Map(),
    claims: [],
    transactions: [],
    runs: [],
    priority: [],
    acquisitions: [],
    planned: [],
    lastRunAt: 0,
  };
}

/**
 * An in-season league's waiver history: last week's drops and claims,
 * processed by the real engine at this period's opening run, then this
 * week's plan, including one claim of mine placed Friday evening so the
 * screen has a claim to show and Home has a real pending count.
 */
export function initWaivers(state: SimState): void {
  const ws = state.waiverState;
  // A rolling order starts at the reverse of the draft and moves only when a
  // team wins (rule 2). A league seeded in week 3 has no runs of weeks 1 and 2
  // to replay, so the standings stand in for where two weeks of wins would have
  // left it; at 0–0 they are the reverse of the draft (`standingRows`).
  ws.priority = reverseStandingsOrder(standingRows(state, state.periodStart));
  if (state.status !== 'in-season' || state.settings.waivers.system === 'none') return;
  const opening = lastProcessingAt(
    state.settings.waivers.processing,
    zone(state),
    state.periodStart + 6 * H,
  );
  if (opening === null) {
    ws.lastRunAt = state.periodStart;
    planWeek(state, state.periodStart);
    return;
  }
  const previous =
    lastProcessingAt(state.settings.waivers.processing, zone(state), opening - 1) ??
    opening - 7 * 24 * H;
  ws.lastRunAt = previous;
  planWeek(state, previous);
  // Everything planned for last week happens, then the opening run decides the claims.
  const lastWeek = ws.planned.filter((m) => m.at <= opening).sort((a, b) => a.at - b.at);
  ws.planned = ws.planned.filter((m) => m.at > opening);
  // LAST WEEK'S BOX SCORE IS ITS LINEUPS AT ITS SUNDAY KICKOFF (B-527): a
  // move made before it played that week, one after it did not.
  const kickoff = state.sundayUtcMidnight - 7 * 24 * H + 17 * H;
  let kept = false;
  for (const m of lastWeek) {
    if (!kept && m.at >= kickoff) {
      keepWeek(state, state.currentPeriod - 1);
      kept = true;
    }
    executePlanned(state, m);
  }
  if (!kept) keepWeek(state, state.currentPeriod - 1);
  runWaivers(state, opening);
  planWeek(state, opening);
  // Mine: Friday 7 PM, on the best player who will be on waivers by then, dropping my thinnest bench player.
  const mine = myTeam(state);
  const claimAt = opening + 64 * H;
  const byThen = ws.planned
    .filter((m) => m.at < claimAt)
    .map((m) => (m.kind === 'drop' ? m.playerId : m.kind === 'add' ? m.dropPlayerId : null))
    .filter((id): id is string => id !== null);
  const waiting = [
    ...[...ws.onWaivers.entries()].filter(([, e]) => e.clearsAt > opening).map(([id]) => id),
    ...byThen,
  ]
    .map((id) => state.players.get(id)!)
    .filter((p) => !p.injury && !state.byes.includes(p.proTeam))
    .sort((a, b) => b.ppg - a.ppg);
  const target = waiting[0];
  const drop = worstBench(state, mine, new Set());
  if (target && drop)
    ws.planned.push({
      at: claimAt,
      kind: 'claim',
      teamId: mine.id,
      playerId: target.id,
      bid: state.settings.waivers.system === 'faab' ? Math.min(mine.faabRemaining, 12) : null,
      dropPlayerId: drop,
    });
}

/** After a draft completes: every undrafted player sits on waivers for the post-draft period. */
export function startWaiversAfterDraft(state: SimState, now: number): void {
  const ws = state.waiverState;
  // The team that drafted last claims first (rule 2). This reversed the list of
  // teams until W3, which is not the draft's order unless the seats happen to be.
  ws.priority = reverseDraftOrder(state);
  ws.lastRunAt = now;
  const rules = state.settings.waivers;
  if (rules.system === 'none') return;
  const owned = new Set(state.teams.flatMap((t) => t.roster));
  const clearsAt = clearsAtAfterDraft(rules, zone(state), now);
  for (const p of state.players.values())
    if (!owned.has(p.id)) ws.onWaivers.set(p.id, { clearsAt, droppedBy: null, droppedAt: now });
  planWeek(state, now);
}

// ── Views ─────────────────────────────────────────────────────────────────

/** The log's and the chat's sentence for a move: `@fsp/waivers`' one copy, which a real league says too. */
export function transactionSummary(state: SimState, tx: Transaction): string {
  const team = state.teams.find((t) => t.id === tx.teamId);
  return summarizeMove(
    tx,
    team?.ownerName ?? 'Someone',
    (id) => state.players.get(id)?.name ?? 'somebody',
  );
}

function transactionView(state: SimState, tx: Transaction, now: number): TransactionView {
  const team = state.teams.find((t) => t.id === tx.teamId)!;
  const summarize = (id: string) => playerSummary(state, state.players.get(id)!);
  const undoable = team.isMine && !undoProblem(state, tx, now);
  return {
    id: tx.id,
    at: iso(tx.at),
    kind: tx.kind,
    team: teamRef(state, team, now),
    added: tx.added.map(summarize),
    dropped: tx.dropped.map(summarize),
    faab: tx.faab,
    reason: tx.reason,
    summary: transactionSummary(state, tx),
    reversed: tx.reversedBy !== null,
    // The practice league has no commissioner: its only take-back is the undo.
    reversal: tx.reversedBy !== null ? 'undo' : null,
    undoableUntil: undoable ? iso(tx.at + UNDO_MS) : null,
    claimId: tx.claimId,
    runId: tx.runId,
    tradeId: tx.tradeId ?? null,
  };
}

function runView(state: SimState, run: WaiverRun, now: number): LogEntry {
  const results = run.results.map((r): ClaimResultView => {
    const team = state.teams.find((t) => t.id === r.teamId)!;
    const winner = r.facts.winningTeamId
      ? state.teams.find((t) => t.id === r.facts.winningTeamId)
      : null;
    const isMine = team.isMine;
    return {
      claimId: r.claimId,
      team: teamRef(state, team, now),
      player: playerSummary(state, state.players.get(r.playerId)!),
      drops: r.facts.dropPlayerIds.map((id) => playerSummary(state, state.players.get(id)!)),
      outcome: r.outcome,
      sentence: r.sentence,
      isMine,
      // Every team's bid: a run that is over shows them all (B-136, not B-28).
      bid: r.facts.bid,
      winningBid: r.facts.winningBid,
      winningTeam: winner ? teamRef(state, winner, now) : null,
      priorityRank: r.facts.priorityRank,
      winnerRank: r.facts.winnerRank,
      budgetLeft: r.facts.budgetLeft,
      // Nobody reverses a practice league's claim (J2 is a real league's).
      transactionId: null,
      reversed: false,
    };
  });
  // My lines first inside a run, then the rest in decision order.
  results.sort((a, b) => Number(b.isMine) - Number(a.isMine));
  const won = results.filter((r) => r.outcome === 'won').length;
  return {
    kind: 'run',
    at: iso(run.at),
    run: {
      id: run.id,
      at: iso(run.at),
      claimCount: results.length,
      wonCount: won,
      results,
      summary: runSummary(won, results.length),
    },
  };
}

function priorityView(state: SimState, now: number): WaiversView['priority'] {
  const rules = state.settings.waivers;
  const mine = myTeam(state);
  const of = state.teams.length;
  const rows = standingRows(state, now);
  const source = prioritySource(rules, rows, state.currentPeriod > state.regularSeasonEnd);
  if (source === null) return null;
  const rank = waiverOrder(state, now).indexOf(mine.id) + 1;
  const rec = teamRef(state, mine, now).record;
  return {
    rank,
    of,
    source,
    why: priorityWhy({
      rules,
      source,
      rank,
      of,
      record: { wins: rec.wins, losses: rec.losses },
      draftedAt: draftOrder(state).indexOf(mine.id) + 1,
    }),
  };
}

function claimView(state: SimState, c: Claim, now: number): ClaimView {
  const team = state.teams.find((t) => t.id === c.teamId)!;
  const p = state.players.get(c.playerId)!;
  const mine = pendingOf(state, team.id);
  const problem = claimProblem(
    {
      rules: state.settings.waivers,
      limits: state.settings.acquisitions,
      teamId: team.id,
      roster: team.roster,
      locked: lockedPlayers(state, now),
      acquisitionsUsed: used(state, team.id),
      playerId: c.playerId,
      bid: c.bid,
      dropPlayerIds: c.dropPlayerIds,
      conditionalOnClaimId: c.conditionalOnClaimId,
      budget: team.faabRemaining,
      myClaims: mine,
      editingClaimId: c.id,
      onWaivers: true,
      decidedAt: claimDecidedAt(state, c.playerId, now),
      lockEndsAt: state.periodEnd,
    },
    waiverContext(state, now),
  );
  const parent = c.conditionalOnClaimId ? mine.find((x) => x.id === c.conditionalOnClaimId) : null;
  // Against the whole budget: a pending claim holds back nothing (rule 5).
  // Until W3 each claim was read against what the earlier ones left.
  const standing = claimStanding(state.settings.waivers, problem, c.bid, team.faabRemaining);
  return {
    id: c.id,
    player: playerSummary(state, p),
    game: gameStatus(state, p, now),
    projection: projection(state, p),
    bid: c.bid,
    drops: c.dropPlayerIds.map((id) => playerSummary(state, state.players.get(id)!)),
    order: c.order,
    conditionalOn: parent
      ? { claimId: parent.id, playerName: state.players.get(parent.playerId)?.name ?? '' }
      : null,
    placedAt: iso(c.placedAt),
    processesAt: (() => {
      const clears =
        waiverClearsAt(state, c.playerId, now) ??
        state.waiverState.onWaivers.get(c.playerId)?.clearsAt ??
        null;
      const at = processesAt(state, Math.max(now, (clears ?? now) - 1));
      return at === null ? (clears === null ? null : iso(clears)) : iso(at);
    })(),
    standing,
    problem,
  };
}

/** The practice league's whole log, newest first: every run, and every move outside one. */
function wholeLog(state: SimState, now: number): LogEntry[] {
  const ws = state.waiverState;
  return [
    ...ws.runs.map((run) => runView(state, run, now)),
    ...ws.transactions
      .filter((tx) => tx.runId === null)
      .map((tx) => ({
        kind: 'transaction' as const,
        at: iso(tx.at),
        transaction: transactionView(state, tx, now),
      })),
  ].sort((a, b) => b.at.localeCompare(a.at));
}

/**
 * The practice league's log older than `waiversView`'s, a page at a time
 * (B-526), as `GET …/transactions?filter=all&cursor=` pages a real league's.
 * Its cursor is the place the page starts at, which the simulator's log, held
 * whole in its state, can name; null past the end.
 */
export function waiversLogPage(
  state: SimState,
  now: number,
  cursor: string,
  limit = WAIVERS_LOG_LIMIT,
): LogPage {
  const log = wholeLog(state, now);
  const from = Math.max(0, Number.parseInt(cursor, 10) || 0);
  const to = from + limit;
  return { items: log.slice(from, to), nextCursor: log.length > to ? String(to) : null };
}

export function waiversView(state: SimState, now: number): WaiversView {
  const ws = state.waiverState;
  const rules = state.settings.waivers;
  const mine = myTeam(state);
  const myPending = pendingOf(state, mine.id);
  const locked = lockedPlayers(state, now);
  const owned = new Set(state.teams.flatMap((t) => t.roster));

  const onWaivers: WaiverPlayerRow[] = [...ws.onWaivers.entries()]
    .filter(([id]) => !owned.has(id))
    .map(([id, e]) => {
      const p = state.players.get(id)!;
      // A cleared player whose game has started is back on waivers until the run (rule 6).
      const clears = waiverClearsAt(state, id, now);
      const cleared = clears === null;
      const myClaim = myPending.find((c) => c.playerId === id) ?? null;
      const room = canMakeRoom(
        {
          rules,
          limits: state.settings.acquisitions,
          teamId: mine.id,
          roster: mine.roster,
          locked,
          acquisitionsUsed: used(state, mine.id),
          playerId: id,
        },
        waiverContext(state, now),
      );
      const r = rng(hashSeed(p.id, 'own'));
      const game = gameStatus(state, p, now);
      return {
        player: playerSummary(state, p),
        game,
        projection: projection(state, p),
        basis: basisFor(state, p, game),
        // The tour plays one week: no season to add up.
        seasonAvg: null,
        ownershipPct: Math.max(1, Math.min(100, Math.round(p.ppg * 5 + r.around(0, 12)))),
        clearsAt: iso(clears ?? e.clearsAt),
        cleared,
        droppedBy: e.droppedBy,
        myClaimId: myClaim?.id ?? null,
        action: (cleared ? (room.ok ? 'add' : 'none') : 'claim') as WaiverPlayerRow['action'],
        reason: cleared && !room.ok ? room.reason : null,
      };
    })
    .sort(
      (a, b) =>
        Number(a.cleared) - Number(b.cleared) ||
        a.clearsAt.localeCompare(b.clearsAt) ||
        (b.projection ?? -Infinity) - (a.projection ?? -Infinity),
    );

  const log = wholeLog(state, now);

  const u = used(state, mine.id);
  const next = processesAt(state, now);
  return {
    league: leagueRef(state),
    timeZone: labelZone(state),
    system: rules.system,
    headline: processingHeadline(rules, {
      next,
      zone: labelZone(state),
      leagueZone: zone(state),
      at: now,
    }),
    processesAt: next === null ? null : iso(next),
    budget:
      rules.system === 'faab' ? { left: mine.faabRemaining, total: rules.faab?.budget ?? 0 } : null,
    priority: priorityView(state, now),
    myClaims: myPending.map((c) => claimView(state, c, now)),
    onWaivers: onWaivers.slice(0, ON_WAIVERS_LIMIT),
    onWaiversTruncated: onWaivers.length > ON_WAIVERS_LIMIT,
    log: log.slice(0, WAIVERS_LOG_LIMIT),
    logTruncated: log.length > WAIVERS_LOG_LIMIT,
    logCursor: log.length > WAIVERS_LOG_LIMIT ? String(WAIVERS_LOG_LIMIT) : null,
    acquisitions: {
      usedThisPeriod: u.period,
      perPeriod: state.settings.acquisitions.perPeriod,
      usedThisSeason: u.season,
      perSeason: state.settings.acquisitions.perSeason,
    },
    ...orderView(state, now),
    updatedAt: iso(now),
  };
}

/**
 * The league's order and budgets, for everybody (J7). The practice league has
 * no commissioner, so nothing in it can be changed by hand.
 */
function orderView(
  state: SimState,
  now: number,
): Pick<WaiversView, 'order' | 'orderSource' | 'commissioner'> {
  const rules = state.settings.waivers;
  const budgets = rules.system === 'faab';
  const source = prioritySource(
    rules,
    standingRows(state, now),
    state.currentPeriod > state.regularSeasonEnd,
  );
  if (source === null && !budgets) return { order: [], orderSource: null, commissioner: null };
  const ids = source === null ? state.teams.map((t) => t.id) : waiverOrder(state, now);
  return {
    order: ids.map((id, i) => {
      const team = state.teams.find((t) => t.id === id)!;
      return {
        team: teamRef(state, team, now),
        rank: source === null ? null : i + 1,
        budgetLeft: budgets ? team.faabRemaining : null,
      };
    }),
    orderSource: source,
    commissioner: null,
  };
}

/**
 * The consequence line for dropping this player, or null when nothing needs
 * saying. `to` is where the player would go, given only when the option can be
 * picked: a recent add skipping waivers is said here, before the move, because
 * it is the one destination that differs from the league's usual.
 */
function dropConsequence(
  state: SimState,
  team: SimTeam,
  p: SimPlayer,
  locked: boolean,
  game: GameStatus,
  to: DropTo | null,
): string | null {
  const pos = p.positions[0];
  const same = team.roster.filter((id) => state.players.get(id)!.positions[0] === pos).length;
  const row = team.lineup.find((r) => r.playerId === p.id);
  const starting = row ? slotDefs.get(row.slot)?.kind === 'starter' : false;
  return consequenceOf({
    locked,
    recentAdd: to?.kind === 'free-agency' && to.because === 'recent-add',
    onlyAt: same === 1 ? pos : null,
    startingAt: starting ? (slotDefs.get(row!.slot)?.name ?? row!.slot) : null,
    bye: game.state === 'bye',
    out: p.injury?.status === 'O',
  });
}

/** Everything the add, claim, and drop sheets need for one player. */
export function rosterMoveView(
  state: SimState,
  kind: 'add' | 'claim' | 'drop',
  playerId: string,
  now: number,
  claimId: string | null = null,
): RosterMoveView {
  const mine = myTeam(state);
  const p = state.players.get(playerId)!;
  const rules = state.settings.waivers;
  const locked = lockedPlayers(state, now);
  const ctx = waiverContext(state, now);
  const editing = claimId
    ? (pendingOf(state, mine.id).find((c) => c.id === claimId) ?? null)
    : null;
  const others = pendingOf(state, mine.id).filter((c) => c.id !== editing?.id);
  const e = state.waiverState.onWaivers.get(playerId);
  const clears = waiverClearsAt(state, playerId, now);
  const next = processesAt(state, Math.max(now, (clears ?? e?.clearsAt ?? now) - 1));
  // When a drop in this move happens: now, or for a claim the run that decides
  // it — a continuous league's comes when the player clears.
  const dropAt = kind === 'claim' ? claimDecidedAt(state, playerId, now) : now;
  // Whether a started player of mine can be this claim's drop: only when the
  // lock has lifted by the run (`dropLockHolds`). The default Wednesday run's
  // has; a run inside the period, such as a daily one, has not.
  const lockLiftsFirst = kind === 'claim' && !dropLockHolds(rules, dropAt, state.periodEnd);
  const roster: DropOption[] = mine.lineup
    .filter((r) => r.playerId)
    .map((r) => {
      const rp = state.players.get(r.playerId!)!;
      const game = gameStatus(state, rp, now);
      const isLocked = locked.has(rp.id);
      const droppable = !isLocked || lockLiftsFirst;
      const to = dropTo(state, mine.id, rp.id, dropAt);
      return {
        player: playerSummary(state, rp),
        game,
        projection: projection(state, rp),
        slot: r.slot,
        starting: slotDefs.get(r.slot)?.kind === 'starter',
        locked: isLocked,
        consequence: dropConsequence(state, mine, rp, isLocked, game, droppable ? to : null),
        droppable,
        dropsTo: dropToView(to),
      };
    })
    .sort(
      (a, b) =>
        Number(a.starting) - Number(b.starting) ||
        (a.projection ?? Infinity) - (b.projection ?? Infinity),
    );
  const moveInput = {
    rules,
    limits: state.settings.acquisitions,
    teamId: mine.id,
    roster: mine.roster,
    locked,
    acquisitionsUsed: used(state, mine.id),
    playerId,
  };
  let blocked: string | null = null;
  if (kind === 'drop') blocked = dropProblem(moveInput, ctx);
  else {
    // A claim decided after the game-time lock lifts is not blocked by it.
    // One decided while the lock holds is, for its drops. The claimed player
    // himself is locked only when his own game has started: on a schedule he
    // then clears after the period (`waiverClearsAt`), so his claim is decided
    // after the lock; on a continuous one the refusal is right, because his
    // claim would be decided `locked`.
    const room = canMakeRoom(lockLiftsFirst ? { ...moveInput, locked: new Set() } : moveInput, ctx);
    if (!room.ok) blocked = room.reason;
    if (kind === 'add' && clears !== null) {
      // Only the game's start put this one there: say so, or a player who was
      // a free agent this morning reads as a mistake (rule 6).
      blocked = onWaiversSentence(p.name, clears, !(e && e.clearsAt > now), labelZone(state), now);
    }
  }
  const dropsTo = kind === 'drop' ? dropTo(state, mine.id, playerId, now) : null;
  return {
    kind,
    player: playerSummary(state, p),
    game: gameStatus(state, p, now),
    projection: projection(state, p),
    processesAt: next === null ? null : iso(next),
    bid:
      kind === 'claim' && rules.system === 'faab'
        ? {
            min: rules.faab?.minBid ?? 0,
            // The whole budget: a pending claim holds back nothing (rule 5).
            max: Math.max(0, mine.faabRemaining),
            allowZero: rules.faab?.allowZeroBids ?? true,
            left: mine.faabRemaining,
          }
        : null,
    mustDrop: kind !== 'drop' && mine.roster.length >= rosterCapacity(ctx.roster),
    roster,
    otherClaims: others.map((c) => ({
      claimId: c.id,
      playerName: state.players.get(c.playerId)?.name ?? '',
      order: c.order,
    })),
    conditionalAllowed: rules.conditionalClaims && others.length > 0,
    defaultOrder: editing?.order ?? others.length + 1,
    existing: editing
      ? {
          claimId: editing.id,
          bid: editing.bid,
          dropPlayerIds: [...editing.dropPlayerIds],
          order: editing.order,
          conditionalOnClaimId: editing.conditionalOnClaimId,
        }
      : null,
    dropConsequence: dropsTo === null ? null : dropSentence(p.name, dropsTo, labelZone(state), now),
    dropsTo: dropsTo === null ? null : dropToView(dropsTo),
    blocked,
  };
}

/** Home's "claims pending" card, from real claims. */
export function pendingClaimsSummary(
  state: SimState,
  now: number,
): { count: number; processesAt: number | null } {
  if (state.status !== 'in-season') return { count: 0, processesAt: null };
  const mine = myTeam(state);
  const count = pendingOf(state, mine.id).length;
  return { count, processesAt: processesAt(state, now) };
}
